import type { DateRange, FormType, FormTypeValues, Paging, PeriodicReviewConfig, Translations } from '@pbk-web/types';

import type { RiskRating } from '../utils/RiskUtils';
import type { Access } from './Access';
import type { ClientFormSectionStatus } from './ClientFormSectionStatus';
import type { ClientFormStatus } from './ClientFormStatus';
import type { ClientFormUserRoleValues } from './ClientFormUserRoles';
import type { VersionState } from './ClientFormVersionHistory';
import type { DistributionResponse, DistributionStatusFilter } from './Distribution';
import type { ActionPlaceholderData, FormConfig, PeriodicReviewChecklistItemInstance, Progress } from './Form';
import type { PeriodicReviewStatus } from './PeriodicReview';
import type { WorkflowTriggerResponse } from './WorkflowTriggerResponse';

export type ClientForm = {
  id: string;
  customIdentifier: string;
  clientId: string;
  clientName: string;
  dueDateUtc: string | null;
  startDateUtc: string | null;
  status: ClientFormStatus;
  statusUtc: string | null;
  subtitle: string;
  totalActionsAnswered: number;
  form: FormConfig;
  clientModuleId: string;
  clientModuleSectionId: string;
  parentId: string | null;
  overallProgress: number;
  totalRequiredActions: number;
  totalRequiredAnswers: number;
  type: (typeof FormType)[keyof typeof FormType];
  totalRiskScore: number;
  majorVersion: number;
  minorVersion: number;
  isShared: boolean;
  readonly isPublic: boolean;
  modifiedUtc: string;
  sectionStatuses: ClientFormSectionInfo[];
  sectionProgress: Record<
    string,
    {
      requiredActions: number;
      requiredAnswers: number;
      requiredChildActions: number;
      requiredChildAnswers: number;
    }
  >;
  allSectionsValid: boolean;
  archivedUtc?: string;
  createdUtc: string;
  periodicReviewConfig?: PeriodicReviewConfig;
  periodicReviewChecklist?: PeriodicReviewChecklistItemInstance[];
  periodicReviewStatus?: PeriodicReviewStatus;
  periodicReviewCount: number;
  accessType: Access;
  attachments?: Attachment[];
  placeholders?: Record<string, ActionPlaceholderData>;
  originId: string | null;
  effectiveDateUtc: string | null;
  distribution?: DistributionResponse;
} & PeriodicReviewConfigForGets;

export type PeriodicReviewConfigForGets = {
  nextReviewStartDateUtc?: string;
  periodicReviewDueDate?: string;
  periodicReviewStatus?: PeriodicReviewStatus;
  lastReviewCompletedDateUtc?: string;
  periodicReviewCreatedDateUtc?: string;
  periodicReviewDueDateUtc?: string;
};

export type ClientFormSectionInfo = {
  id: string;
  templateFormSectionId: string;
  clientFormId: string;
  status: ClientFormSectionStatus;
  statusUtc: string;
  statusChangedBy: string;
  workflowTriggerResponse: WorkflowTriggerResponse;
  totalRequiredAnswers: number;
  totalRequiredActions: number;
  allSectionsValid: boolean;
  placeholders: { addedLookups: Record<string, ActionPlaceholderData>; removedLookups: Record<string, ActionPlaceholderData> };
};

export type ClientFormRequest = {
  templateFormId: string;
  templateFormVersion?: number;
  clientId: string;
  subtitle: string | null;
  clientModuleId?: string;
  clientModuleSectionId?: string;
  parentId?: string;
  parentSectionId?: string;
  parentActionId?: string;
  // Provenance only: the form whose association question this form is being created to answer.
  // Unrelated to the parent* fields above, which carry sub-form hierarchy semantics.
  createdFromClientFormId?: string;
  ownerId?: string;
  dueDateUtc?: Date | null;
  startDateUtc?: Date | null;
  allowNoOwner?: boolean;
  effectiveDateUtc?: Date | null;
};

export type ActivityFilter = {
  types?: (typeof FormType)[keyof typeof FormType][];
  isArchived?: boolean;
  picklistIds?: Record<string, string[]>;
  roles?: ClientFormUserRoleValues[];
  title?: string;
  formStatus?: ClientFormStatus[];
  riskStatuses?: RiskRating[];
  dueDateRange?: DateRange | null;
  users?: string[];
  templateIds?: string[];
  clientModuleId?: string;
  clientModuleSectionId?: string;
  templateModuleId?: string;
  templateModuleSectionId?: string;
  excludedFormIds?: string[];
  includedFormIds?: string[];
  countReferences?: FormReferenceDirection;
  countAttachments?: boolean;
  versionState?: VersionState;
  isKnowledgeGraphForm?: boolean;
  distributionStatus?: DistributionStatusFilter[];
  periodicReviewStatuses?: PeriodicReviewStatus[];
  importId?: string | null;
  containsDirtyTemplateReference?: boolean;
  selectedIds?: string[]; // Only GQL
  includeNonLatest?: boolean; // Only GQL
};

export type DataExportActivityFilter = ActivityPagedFilter & {
  excludedAttachmentIds?: Record<string, string[]>;
  excludedReferenceIds?: Record<string, string[]>;
  excludedTemplateReferenceIds?: Record<string, string[]>;
};

export type ActivityPagedFilter = ActivityFilter & Paging;

export enum FormReferenceDirection {
  Source = 0,
  Target = 1,
  Both = 2,
}

export type ClientFormProgressResponse = {
  form?: number;
  formOverall: number;
  totalRequiredActions: number;
  totalRequiredAnswers: number;
  sectionProgress: Record<string, Progress>;
};

export type Attachment = {
  sortOrder: number;
  fileName: string;
  fileId: string;
  fileFormat: string;
  clientFormId: string;
  formActionId?: string;
  formAnswerId?: string;
  formSectionId?: string;
};

export type SignatureDetails = {
  signerName: string;
  password?: string;
  twoFactor?: string;
};

export type SignResponse = {
  success: boolean;
  redirectUrl?: string;
};

export type ClientFormTitleResponse = {
  id: string;
  subtitle: string;
  documentNumber: number | null;
  templateModuleTranslations: Translations;
};

// Meta data used in lists of forms, without the full form data
export type ClientFormMetaDataResponse = {
  id: string;
  originId: string | null;
  clientId: string;
  clientName: string;
  templateId: string;
  majorVersion: number;
  dueDateUtc?: string | null;
  translations: Translations;
  templateModuleTranslations: Translations;
  type: FormTypeValues;
  documentNumber: number;
  isSystem: boolean;
  parentId: string | null;
  hasShares: boolean;
  status: ClientFormStatus;
  requiresApproval: boolean;
  requiresValidation: boolean;
  archivedUtc?: string | null;
  nextReviewStartDateUtc?: string;
  periodicReviewDueDateUtc?: string;
  periodicReviewStatus?: PeriodicReviewStatus;
  lastReviewCompletedDateUtc?: string;
  periodicReviewCreatedDateUtc?: string;
  canAccess: boolean;
};
